// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Ensure cookies (session_id) are always sent with every request
axios.defaults.withCredentials = true;

// Axios interceptor: automatically attach x-school-id and x-impersonate-user-id headers
// Priority: impersonated school (sessionStorage) > user's own school (localStorage)
axios.interceptors.request.use((config) => {
    const impersonated = sessionStorage.getItem('impersonatedSchool');
    if (impersonated) {
        const { id } = JSON.parse(impersonated);
        config.headers['x-school-id'] = String(id);
    } else {
        const schoolId = localStorage.getItem('schoolId');
        if (schoolId) {
            config.headers['x-school-id'] = schoolId;
        }
    }
    // Attach impersonated user header when SuperAdmin is viewing as a specific user
    const impersonatedUser = sessionStorage.getItem('impersonatedUser');
    if (impersonatedUser) {
        const { id } = JSON.parse(impersonatedUser);
        config.headers['x-impersonate-user-id'] = id;
    }
    return config;
});

// Trial ended / subscription lapsed → the API answers 402 with a code. Send the
// user to the upgrade page instead of letting every screen show a generic error.
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const code = error?.response?.status === 402 ? error.response.data?.code : null;
        if ((code === 'trial_expired' || code === 'subscription_canceled') && window.location.pathname !== '/billing') {
            window.location.assign('/billing');
        }
        return Promise.reject(error);
    }
);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [impersonatedSchool, setImpersonatedSchool] = useState(() => {
        const stored = sessionStorage.getItem('impersonatedSchool');
        return stored ? JSON.parse(stored) : null;
    });
    const [impersonatedUser, setImpersonatedUser] = useState(() => {
        const stored = sessionStorage.getItem('impersonatedUser');
        return stored ? JSON.parse(stored) : null;
    });

    useEffect(() => {
        // Check if the user is authenticated when the component mounts
        axios.get('/api/auth/check')
            .then(response => {
                if (response.data.isAuthenticated) {
                    setIsAuthenticated(true);
                    setUser(response.data.user);
                    // Persist schoolId for the Axios interceptor
                    if (response.data.user?.schoolId) {
                        localStorage.setItem('schoolId', response.data.user.schoolId);
                    }
                }
            })
            .catch(error => {
                console.error('Error checking authentication', error);
            })
            .finally(() => setAuthLoading(false));
    }, []);

    const login = (credentials) => {
        return axios.post('/api/auth/login', credentials)
            .then(response => {
                if (response.data.success) {
                    setIsAuthenticated(true);
                    setUser(response.data.user);
                    localStorage.setItem("isAuthenticated", true);
                    // Persist schoolId for the Axios interceptor
                    if (response.data.user?.schoolId) {
                        localStorage.setItem('schoolId', response.data.user.schoolId);
                    }
                }
                return response.data;
            });
    };

    const logout = () => {
        return axios.post('/api/auth/logout')
            .then(response => {
                setIsAuthenticated(false);
                localStorage.removeItem("isAuthenticated");
                localStorage.removeItem("schoolId");
                sessionStorage.removeItem('impersonatedSchool');
                sessionStorage.removeItem('impersonatedUser');
                setImpersonatedSchool(null);
                setImpersonatedUser(null);
                setUser(null);
                window.location.href = "/";
            });
    };

    const register = (userData) => {
        return axios.post('/api/auth/register', userData)
            .then(response => {
                if (response.data.success) {
                    setIsAuthenticated(true);
                    setUser(response.data.user);
                }
                return response.data;
            })
            .catch(error => {
                return error.response?.data || { msg: 'Registration failed', success: false };
            });
    };

    const enterSchool = (school) => {
        const schoolData = { id: school.id, schoolName: school.schoolName };
        setImpersonatedSchool(schoolData);
        sessionStorage.setItem('impersonatedSchool', JSON.stringify(schoolData));
    };

    const exitSchool = () => {
        setImpersonatedSchool(null);
        setImpersonatedUser(null);
        sessionStorage.removeItem('impersonatedSchool');
        sessionStorage.removeItem('impersonatedUser');
    };

    const impersonateUser = (targetUser) => {
        const userData = { id: targetUser.id, name: targetUser.name, email: targetUser.email, roleName: targetUser.role?.name || targetUser.roleName };
        setImpersonatedUser(userData);
        sessionStorage.setItem('impersonatedUser', JSON.stringify(userData));
    };

    const stopImpersonation = () => {
        setImpersonatedUser(null);
        sessionStorage.removeItem('impersonatedUser');
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated, user, authLoading, login, logout, register,
            impersonatedSchool, enterSchool, exitSchool,
            impersonatedUser, impersonateUser, stopImpersonation
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
